import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { fmtEur } from "@/lib/format";
import { Plus, Trash2, Save, Send, ArrowLeft, ExternalLink, Sparkles, FileBarChart, Building2, User, Lock } from "lucide-react";
import { toast } from "sonner";

export default function QuoteEdit() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const reload = () => api.get(`/quotes/${id}`).then((r) => setQ(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [id]);

  const aiDescription = async () => {
    if (!q?.simulation_id) { toast.error("Preventivo senza simulazione collegata"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/quote-description", { simulation_id: q.simulation_id });
      setQ({ ...q, notes: (q.notes ? q.notes + "\n\n" : "") + data.description });
      toast.success("Descrizione AI generata");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAiBusy(false); }
  };

  // Verifica prezzo minimo lato client prima di chiamare il backend
  const minViolation = () => {
    for (const it of q.items) {
      if (it.min_price != null && (parseFloat(it.unit_price) || 0) + 1e-6 < parseFloat(it.min_price)) {
        return `«${it.description}»: prezzo sotto il minimo (${fmtEur(it.min_price)}).`;
      }
    }
    const sub = q.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
    const floor = q.items.reduce((s, i) => s + (i.min_price != null ? (parseFloat(i.quantity) || 0) * parseFloat(i.min_price) : 0), 0);
    if (floor > 0 && (sub - parseFloat(q.discount || 0)) + 1e-6 < floor) {
      return `Lo sconto porta il totale sotto il minimo consentito (${fmtEur(floor)}).`;
    }
    return null;
  };

  const save = async () => {
    const v = minViolation();
    if (v) { toast.error(v); return false; }
    try {
      await api.patch(`/quotes/${id}`, { items: q.items, discount: parseFloat(q.discount), tax_rate: parseFloat(q.tax_rate), notes: q.notes, status: q.status, profile: q.profile, gazebo_cost: parseFloat(q.gazebo_cost) || 0 });
      toast.success("Salvato"); reload();
      return true;
    } catch (e) { toast.error(formatErr(e)); return false; }
  };

  const send = async () => {
    if (!(await save())) return;
    const { data } = await api.post(`/quotes/${id}/send`);
    window.location.href = data.mailto;
    toast.success("Preventivo inviato");
    reload();
  };

  // Apre il PDF (preventivo o business plan) in una nuova scheda, vista inline.
  // La scheda viene aperta subito (gesto utente) per evitare il blocco popup,
  // poi vi carichiamo il blob autenticato.
  const openPdfTab = async (path) => {
    const token = localStorage.getItem("sf_token");
    if (!token) { toast.error("Sessione scaduta — effettua di nuovo l'accesso"); return; }
    const tab = window.open("", "_blank");
    try {
      const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { if (tab) tab.close(); toast.error("Impossibile generare il PDF"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (tab) tab.close(); toast.error(formatErr(e)); }
  };
  const openBusinessPlan = () => openPdfTab(`/quotes/${id}/business-plan/pdf`);

  const updItem = (i, k, v) => { const it = [...q.items]; it[i] = { ...it[i], [k]: k === "description" ? v : parseFloat(v) || 0 }; setQ({ ...q, items: it }); };
  const addItem = () => setQ({ ...q, items: [...q.items, { description: "Nuova voce", quantity: 1, unit_price: 0 }] });
  const delItem = (i) => setQ({ ...q, items: q.items.filter((_, j) => j !== i) });

  if (!q) return <div className="p-8 text-zinc-500">Caricamento…</div>;
  const subtotal = q.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const tax = Math.max(0, subtotal - parseFloat(q.discount || 0)) * parseFloat(q.tax_rate || 0);
  const total = Math.max(0, subtotal - parseFloat(q.discount || 0)) + tax;
  const profile = q.profile || "private";
  const v = minViolation();

  return (
    <div data-testid="page-quote-edit">
      <PageHeader eyebrow={q.quote_number} title="Modifica preventivo" subtitle={`Stato: ${q.status}`} actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/app/quotes" className="px-3 py-2 border border-zinc-200/70 rounded-lg text-sm flex items-center gap-1.5"><ArrowLeft size={13}/> Indietro</Link>
          <a href={`/p/${q.public_token}`} target="_blank" rel="noreferrer" className="px-3 py-2 border border-zinc-200/70 rounded-lg text-sm flex items-center gap-1.5 text-sky-600" data-testid="public-link"><ExternalLink size={13}/> Apri online</a>
          <button type="button" onClick={openBusinessPlan} className="px-3 py-2 border border-amber-300 bg-amber-50 rounded-lg text-sm flex items-center gap-1.5 text-amber-700 font-semibold" data-testid="business-plan-btn"><FileBarChart size={13}/> Business Plan</button>
          <button onClick={save} className="px-3 py-2 bg-zinc-100 rounded-lg text-sm flex items-center gap-1.5" data-testid="quote-save"><Save size={13}/> Salva</button>
          <button onClick={send} className="px-3 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5" data-testid="quote-send"><Send size={13}/> Invia email</button>
        </div>
      }/>
      <div className="px-8 py-6 max-w-4xl">
        {/* Profilo preventivo: privato vs azienda */}
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-5 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Profilo preventivo</div>
            <p className="text-xs text-zinc-500">{profile === "business" ? "Azienda — ammortamento e bonus/fondo perduto nel business plan." : "Privato — detrazione fiscale (36% / 50%) nel business plan."}</p>
          </div>
          <div className="inline-flex bg-zinc-100 rounded-xl p-1">
            {[["private", "Privato", User], ["business", "Azienda", Building2]].map(([val, lbl, Ic]) => (
              <button key={val} onClick={() => setQ({ ...q, profile: val })}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${profile === val ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
                data-testid={`profile-${val}`}>
                <Ic size={14}/> {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-200/70 rounded-2xl p-5 mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Voci preventivo</div>
          <div className="space-y-2">
            {q.items.map((it, i) => {
              const below = it.min_price != null && (parseFloat(it.unit_price) || 0) + 1e-6 < parseFloat(it.min_price);
              return (
                <div key={i} data-testid={`item-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <input value={it.description} onChange={(e) => updItem(i, "description", e.target.value)} className="col-span-7 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" />
                    <input type="number" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} className="col-span-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-2 py-2 text-sm font-mono text-right" />
                    <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updItem(i, "unit_price", e.target.value)} className={`col-span-2 bg-zinc-50 border rounded-lg px-2 py-2 text-sm font-mono text-right ${below ? "border-rose-400 text-rose-600" : "border-zinc-200/70"}`} />
                    <div className="col-span-1 font-mono text-sm text-right text-amber-600">{fmtEur(it.quantity * it.unit_price)}</div>
                    <button onClick={() => delItem(i)} className="col-span-1 text-rose-400 hover:bg-zinc-100 rounded p-2"><Trash2 size={14}/></button>
                  </div>
                  {it.min_price != null && (
                    <div className={`col-span-12 mt-0.5 ml-1 flex items-center gap-1 text-[10px] ${below ? "text-rose-500" : "text-zinc-400"}`}>
                      <Lock size={9}/> minimo {fmtEur(it.min_price)}{below ? " — sotto il minimo imposto" : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={addItem} className="mt-3 text-xs text-amber-600 flex items-center gap-1" data-testid="add-item-btn"><Plus size={13}/> Aggiungi voce</button>
        </div>

        <div className="bg-white border border-zinc-200/70 rounded-2xl p-5 mb-4 grid grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Sconto €</label>
            <input type="number" step="0.01" value={q.discount} onChange={(e) => setQ({ ...q, discount: e.target.value })} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 font-mono text-sm" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">IVA (es. 0.10 = 10%)</label>
            <input type="number" step="0.01" value={q.tax_rate} onChange={(e) => setQ({ ...q, tax_rate: e.target.value })} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 font-mono text-sm" />
          </div>
          <div className="col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Supplemento gazebo € (fuori dal "chiavi in mano")</label>
            <input type="number" step="0.01" min="0" value={q.gazebo_cost || 0} onChange={(e) => setQ({ ...q, gazebo_cost: e.target.value })} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 font-mono text-sm" data-testid="gazebo-cost" />
          </div>
          <div className="col-span-2">
            <div className="flex items-center justify-between">
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Note / Descrizione commerciale</label>
              <button onClick={aiDescription} disabled={aiBusy} className="text-xs text-emerald-600 flex items-center gap-1 hover:text-amber-600 disabled:opacity-50" data-testid="ai-description-btn">
                <Sparkles size={12}/> {aiBusy ? "Generazione AI…" : "✨ Genera con AI"}
              </button>
            </div>
            <textarea value={q.notes || ""} onChange={(e) => setQ({ ...q, notes: e.target.value })} rows={6} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        {v && (
          <div className="bg-rose-50 border border-rose-200 text-rose-600 rounded-2xl px-5 py-3 mb-4 text-sm flex items-center gap-2" data-testid="min-price-warning">
            <Lock size={14}/> {v} Il prezzo può solo salire rispetto al minimo imposto dall'azienda.
          </div>
        )}

        <div className="bg-white border border-zinc-200/70 rounded-2xl p-5 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Subtotale</span><span className="font-mono">{fmtEur(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Sconto</span><span className="font-mono">– {fmtEur(q.discount)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">IVA</span><span className="font-mono">{fmtEur(tax)}</span></div>
          <div className="flex justify-between text-2xl font-mono pt-3 border-t border-zinc-200/70 mt-3"><span>Totale{parseFloat(q.gazebo_cost) > 0 ? " chiavi in mano" : ""}</span><span className="text-amber-600">{fmtEur(total)}</span></div>
          {parseFloat(q.gazebo_cost) > 0 && (
            <>
              <div className="flex justify-between pt-1"><span className="text-zinc-500">Supplemento gazebo</span><span className="font-mono">{fmtEur(q.gazebo_cost)}</span></div>
              <div className="flex justify-between text-xl font-mono"><span className="font-semibold">Totale con gazebo</span><span className="text-amber-600">{fmtEur(total + (parseFloat(q.gazebo_cost) || 0))}</span></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
